import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../shared/i18n';
import { familyCurrenciesApi } from '../../api/familyCurrencies';
import { CurrencyTransferPanel } from '../../../guardian-dashboard/components/CurrencyTransferPanel';
import { WALLET_NOTIFICATION_COUNT_QUERY_KEY } from '../../../unified-dashboard/hooks/useWalletNotificationCount';
import './FusionEffect.css';

const FusionSparkles = () => {
  // 8 sparkles arranged around a circle converge to the center burst.
  const sparkles = Array.from({ length: 8 }, (_, i) => {
    const angle = (i / 8) * Math.PI * 2;
    const radius = 90;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return { x, y, delay: (i % 4) * 60 };
  });
  return (
    <div
      className="fusion-effect-stage"
      data-testid="fusion-effect-sparkles"
      aria-hidden="true"
    >
      <div className="fusion-effect-center">
        {sparkles.map((s, i) => (
          <div
            key={i}
            className="fusion-sparkle"
            style={{
              '--sparkle-x': `${s.x}px`,
              '--sparkle-y': `${s.y}px`,
              animationDelay: `${s.delay}ms`
            }}
          />
        ))}
        <div className="fusion-effect-burst" />
      </div>
    </div>
  );
};

// Format a duration in minutes for display. Whole hours are rendered as
// "N hour(s)"; everything else falls back to "N minute(s)".
const formatDelay = (minutes, t) => {
  const m = Math.max(0, parseInt(minutes, 10) || 0);
  if (m >= 60 && m % 60 === 0) {
    const hours = m / 60;
    return t('family_currencies.fusion.kid.duration_hours', { hours });
  }
  return t('family_currencies.fusion.kid.duration_minutes', { minutes: m });
};

const FusionSuccessDialog = ({ result, onClose, t }) => {
  const delayed = result.state === 'pending' && (result.delayMinutes || 0) > 0;
  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid="fusion-success-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center py-4">
            <FusionSparkles />
            <h5 className="mb-3">
              {delayed
                ? t('family_currencies.fusion.kid.pending_title')
                : t('family_currencies.fusion.kid.success_title')}
            </h5>
            <p className="mb-4" data-testid="fusion-success-message">
              {delayed
                ? t('family_currencies.fusion.kid.pending_message', {
                    amount: result.amount,
                    name: result.name,
                    duration: formatDelay(result.delayMinutes, t)
                  })
                : t('family_currencies.fusion.kid.success_message', {
                    amount: result.amount,
                    name: result.name
                  })}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
              data-testid="fusion-success-close"
            >
              {t('common.ok')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Format a millisecond delta as HH:MM:SS; negative or NaN values clamp to 00:00:00
// so the countdown reads "ready" the instant scheduled_for is reached.
const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};

const PendingFusionsList = ({ currency, onClaim, claimingId, t }) => {
  const pending = currency.pending_fusions || [];
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (pending.length === 0) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [pending.length]);

  if (pending.length === 0) return null;

  return (
    <div
      className="mt-3 p-3 border rounded-2"
      data-testid={`pending-fusions-${currency.id}`}
    >
      <div className="fw-semibold mb-2">
        {t('family_currencies.fusion.kid.pending_list_title')}
      </div>
      <ul className="list-unstyled mb-0">
        {pending.map((op) => {
          const scheduledAt = op.scheduled_for ? new Date(op.scheduled_for).getTime() : 0;
          const remaining = scheduledAt - now;
          // The backend's `due` flag is authoritative but only refreshes on
          // reload; once the local countdown hits zero we treat the row as due
          // so the Claim button appears without waiting for a refetch.
          const dueNow = op.due || remaining <= 0;
          return (
            <li
              key={op.id}
              className="d-flex align-items-center justify-content-between py-1"
              data-testid={`pending-fusion-${op.id}`}
            >
              <span className="small">
                {t('family_currencies.fusion.kid.pending_row', {
                  amount: op.result_amount,
                  name: currency.name
                })}
                {' · '}
                {dueNow ? (
                  t('family_currencies.fusion.kid.pending_ready')
                ) : (
                  <span data-testid={`pending-countdown-${op.id}`}>
                    {t('family_currencies.fusion.kid.pending_countdown', {
                      countdown: formatCountdown(remaining)
                    })}
                  </span>
                )}
              </span>
              {dueNow && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm ms-2"
                  onClick={() => onClaim(op)}
                  disabled={claimingId === op.id}
                  data-testid={`claim-fusion-${op.id}`}
                >
                  {t('family_currencies.fusion.kid.claim_button')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const FusionPanel = ({ currency, onFuse, busy, t }) => (
  <div
    className="mt-3 p-3 border rounded-2 bg-light"
    data-testid={`fusion-panel-${currency.id}`}
  >
    <div className="fw-semibold mb-2">
      {t('family_currencies.fusion.kid.title')}
    </div>
    <ul className="list-unstyled small mb-3" data-testid={`fusion-panel-components-${currency.id}`}>
      {(currency.fusion_components || []).map((comp) => (
        <li key={comp.id} data-testid={`fusion-component-balance-${currency.id}-${comp.id}`}>
          {comp.name}: {comp.balance}
        </li>
      ))}
    </ul>
    <button
      type="button"
      className="btn btn-primary btn-sm"
      onClick={() => onFuse(currency)}
      disabled={busy}
      data-testid={`fuse-btn-${currency.id}`}
    >
      <i className="bi bi-magic me-1"></i>
      {t('family_currencies.fusion.kid.fuse_button')}
    </button>
  </div>
);

const FusionErrorDialog = ({ error, onClose, t }) => {
  const isComponentEmpty = error.code === 'FUSION_COMPONENT_EMPTY';
  const messageKey = isComponentEmpty
    ? 'family_currencies.fusion.errors.component_empty'
    : 'family_currencies.fusion.errors.generic';
  const messageArgs = isComponentEmpty
    ? { component: error.componentName || '' }
    : { name: error.name };
  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid="fusion-error-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center py-4">
            <div className="mb-3" style={{ fontSize: '3rem' }}>
              <i className="bi bi-exclamation-triangle text-danger"></i>
            </div>
            <h5 className="mb-3">{t('family_currencies.fusion.errors.title')}</h5>
            <p className="mb-4" data-testid="fusion-error-message">
              {t(messageKey, messageArgs)}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
              data-testid="fusion-error-close"
            >
              {t('common.ok')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const WalletPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currencies, setCurrencies] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [fusionOpenId, setFusionOpenId] = useState(null);
  const [fusionBusyId, setFusionBusyId] = useState(null);
  const [fusionSuccess, setFusionSuccess] = useState(null);
  const [fusionError, setFusionError] = useState(null);
  const [claimingFusionId, setClaimingFusionId] = useState(null);

  useEffect(() => {
    // Only show the loading spinner on the initial fetch; subsequent
    // refreshes (e.g. after a fusion) keep the existing cards visible so
    // the page does not appear to reload.
    const isInitial = refreshNonce === 0;
    if (isInitial) setLoading(true);
    familyCurrenciesApi
      .list()
      .then((res) => {
        setCurrencies(res.data?.family_currencies || []);
        queryClient.invalidateQueries({ queryKey: WALLET_NOTIFICATION_COUNT_QUERY_KEY });
      })
      .catch(() => setError(t('family_currencies.errors.generic')))
      .finally(() => {
        if (isInitial) setLoading(false);
      });
  }, [refreshNonce, t, queryClient]);

  const handleFuse = (currency) => {
    setFusionBusyId(currency.id);
    familyCurrenciesApi
      .fuse(currency.id)
      .then((res) => {
        const fusion = res.data?.fusion || {};
        setFusionSuccess({
          amount: fusion.result_amount ?? 0,
          name: currency.name,
          state: fusion.state,
          delayMinutes: fusion.delay_minutes
        });
        setRefreshNonce((n) => n + 1);
      })
      .catch((err) => {
        const errPayload = err.response?.data?.error || {};
        setFusionError({
          code: errPayload.code || 'UNKNOWN',
          name: currency.name,
          componentName: errPayload.details?.component_name || ''
        });
      })
      .finally(() => setFusionBusyId(null));
  };

  const handleClaim = (operation) => {
    setClaimingFusionId(operation.id);
    familyCurrenciesApi
      .claimFusion(operation.id)
      .then(() => {
        setRefreshNonce((n) => n + 1);
      })
      .catch(() => {
        setError(t('family_currencies.errors.generic'));
      })
      .finally(() => setClaimingFusionId(null));
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light">
      <div className="container py-4" data-testid="kid-family-currencies-page">
        <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h2 className="h3 fw-bold mb-0">{t('family_currencies.kid.page_title')}</h2>
          <Link
            to="/wallet/auto-reward-rules"
            className="btn btn-outline-primary btn-sm"
            data-testid="kid-auto-reward-rules-link"
          >
            <i className="bi bi-gift me-1"></i>
            {t('auto_reward_rules.kid.link')}
          </Link>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {currencies.length === 0 && (
          <p className="text-muted">{t('family_currencies.kid.empty')}</p>
        )}
        {currencies.map((currency) => (
          <div className="card mb-3" key={currency.id} data-testid={`kid-currency-card-${currency.id}`}>
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                    data-testid={`currency-icon-${currency.id}`}
                    data-fusion={currency.wallet_type === 'fusion' ? 'true' : 'false'}
                    data-wallet-type={currency.wallet_type || 'positive'}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: currency.wallet_type === 'fusion'
                        ? 'linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)'
                        : currency.wallet_type === 'negative'
                        ? 'linear-gradient(135deg, #fecaca 0%, #dc2626 100%)'
                        : 'linear-gradient(135deg, #bbf7d0 0%, #16a34a 100%)'
                    }}
                  >
                    <i
                      className={`bi ${currency.wallet_type === 'fusion' ? 'bi-magic' : currency.wallet_type === 'negative' ? 'bi-x-lg' : 'bi-check-lg'} text-white`}
                      style={{ fontSize: '22px' }}
                    ></i>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <h5 className="mb-0 text-truncate">{currency.name}</h5>
                    <small className="text-muted" data-testid={`balance-currency-${currency.id}`}>
                      {t('family_currencies.balance', { balance: currency.balance ?? 0 })}
                    </small>
                  </div>
                </div>
                <div
                  className="d-flex flex-wrap align-items-center gap-1 ms-auto justify-content-end"
                  style={{ maxWidth: '100%' }}
                >
                  {currency.wallet_type === 'fusion' && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm flex-shrink-0"
                      onClick={() =>
                        setFusionOpenId((prev) => (prev === currency.id ? null : currency.id))
                      }
                      aria-label={fusionOpenId === currency.id ? t('family_currencies.fusion.kid.close') : t('family_currencies.fusion.kid.open')}
                      title={fusionOpenId === currency.id ? t('family_currencies.fusion.kid.close') : t('family_currencies.fusion.kid.open')}
                      data-testid={`fusion-open-${currency.id}`}
                    >
                      <i className="bi bi-magic"></i>
                      <span className="d-none d-md-inline ms-1">
                        {fusionOpenId === currency.id
                          ? t('family_currencies.fusion.kid.close')
                          : t('family_currencies.fusion.kid.open')}
                      </span>
                    </button>
                  )}
                  {currency.wallet_type !== 'negative' && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm flex-shrink-0"
                      onClick={() =>
                        setOpenId((prev) => (prev === currency.id ? null : currency.id))
                      }
                      aria-label={openId === currency.id ? t('family_currencies.wallet.close') : t('family_currencies.wallet.open')}
                      title={openId === currency.id ? t('family_currencies.wallet.close') : t('family_currencies.wallet.open')}
                      data-testid={`wallet-currency-${currency.id}`}
                    >
                      <i className={`bi ${openId === currency.id ? 'bi-x-lg' : 'bi-arrow-left-right'}`}></i>
                      <span className="d-none d-md-inline ms-1">
                        {openId === currency.id
                          ? t('family_currencies.wallet.close')
                          : t('family_currencies.wallet.open')}
                      </span>
                    </button>
                  )}
                  <Link
                    to={`/wallet/${currency.id}/transactions`}
                    className="btn btn-outline-secondary btn-sm flex-shrink-0"
                    aria-label={t('family_currencies.transactions.link')}
                    title={t('family_currencies.transactions.link')}
                    data-testid={`transactions-link-${currency.id}`}
                  >
                    <i className="bi bi-clock-history"></i>
                    <span className="d-none d-md-inline ms-1">
                      {t('family_currencies.transactions.link')}
                    </span>
                  </Link>
                </div>
              </div>

              {openId === currency.id && (
                <CurrencyTransferPanel
                  currency={currency}
                  api={familyCurrenciesApi}
                  busy={false}
                  onTransferred={() => setRefreshNonce((n) => n + 1)}
                />
              )}

              {currency.wallet_type === 'fusion' && fusionOpenId === currency.id && (
                <FusionPanel
                  currency={currency}
                  onFuse={handleFuse}
                  busy={fusionBusyId === currency.id}
                  t={t}
                />
              )}

              {currency.wallet_type === 'fusion' && (currency.pending_fusions || []).length > 0 && (
                <PendingFusionsList
                  currency={currency}
                  onClaim={handleClaim}
                  claimingId={claimingFusionId}
                  t={t}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {fusionSuccess && (
        <FusionSuccessDialog
          result={fusionSuccess}
          onClose={() => setFusionSuccess(null)}
          t={t}
        />
      )}
      {fusionError && (
        <FusionErrorDialog
          error={fusionError}
          onClose={() => setFusionError(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default WalletPage;
